import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCheck } from 'react-icons/fa6';
import { useGlobalContext } from '../../../context/context';
import axios from 'axios';
import { PageSpinner } from '../../../components/spinner/Spinner';
import toast from 'react-hot-toast';

const TradingPlans = () => {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [user, setUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [investAmount, setInvestAmount] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { baseURL, userData } = useGlobalContext();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, userRes] = await Promise.all([
          axios.get(`${baseURL}/trading-plans`),
          axios.get(`${baseURL}/users/user-info`, {
            headers: { Authorization: `Bearer ${userData?.token}` },
          }),
        ]);
        setPlans(plansRes.data);
        setUser(userRes.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, []);

  const formatAmount = (plan) => {
    if (plan.maxAmount) {
      return `$${plan.minAmount.toLocaleString()} - $${plan.maxAmount.toLocaleString()}`;
    }
    return `$${plan.minAmount.toLocaleString()}+`;
  };

  const openInvestModal = (plan) => {
    setSelectedPlan(plan);
    setInvestAmount(plan.minAmount);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPlan(null);
    setInvestAmount('');
  };

  const handleInvest = async (e) => {
    e.preventDefault();
    const amount = Number(investAmount);

    if (!amount || amount < selectedPlan.minAmount) {
      toast.error(`Minimum investment is $${selectedPlan.minAmount}`);
      return;
    }

    if (selectedPlan.maxAmount && amount > selectedPlan.maxAmount) {
      toast.error(`Maximum investment is $${selectedPlan.maxAmount}`);
      return;
    }

    if (amount > user.deposits) {
      toast.error(`Insufficient balance. You have $${user.deposits.toLocaleString()} available.`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${baseURL}/investments`,
        { planId: selectedPlan._id, amount },
        { headers: { Authorization: `Bearer ${userData?.token}` } }
      );
      toast.success('Investment created successfully!');
      // Update user balance locally
      setUser((prev) => ({ ...prev, deposits: prev.deposits - amount }));
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.msg || 'Failed to create investment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className='content trading-plans-page'>
        <div className='title'>
          <h2>Trading Plans</h2>
        </div>
        <PageSpinner />
      </main>
    );
  }

  return (
    <main className='content trading-plans-page'>
      <div className='title'>
        <h2>Trading Plans</h2>
      </div>

      {user && (
        <div className='balance-info'>
          <p>
            Available Balance: <strong>${user.deposits?.toLocaleString() || 0}</strong>
          </p>
        </div>
      )}

      {plans.length > 0 ? (
        <div className='grid'>
          {plans.map((plan) => {
            const { _id, features, subtitle, title, duration, expectedProfit } = plan;
            return (
              <div className='card' key={_id}>
                <div className='title'>
                  <h3>{title}</h3>
                  <h4>{subtitle}</h4>
                </div>

                <div className='amount'>
                  <h3>{formatAmount(plan)}</h3>
                </div>

                <div className='details'>
                  <p>
                    Duration
                    <strong>{duration} days</strong>
                  </p>
                  <p>
                    Expected Profit
                    <strong>{expectedProfit}%</strong>
                  </p>
                </div>

                <div className='list'>
                  <h3>Features</h3>
                  <ul>
                    {features.map((feature, featureIndex) => {
                      return (
                        <li key={featureIndex}>
                          <FaCheck /> {feature}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className='btn'>
                  <button className='linear' onClick={() => openInvestModal(plan)}>
                    Invest Now
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className='no-data'>
          <h2>No trading plans available at the moment</h2>
        </div>
      )}

      {/* Investment Modal */}
      {showModal && selectedPlan && createPortal(
        <div className='modal-overlay' onClick={closeModal}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h2>Invest in {selectedPlan.title}</h2>
              <button className='close-btn' onClick={closeModal}>
                &times;
              </button>
            </div>

            <div className='modal-body'>
              <div className='plan-summary'>
                <div className='summary-item'>
                  <span>Plan</span>
                  <strong>{selectedPlan.title}</strong>
                </div>
                <div className='summary-item'>
                  <span>Duration</span>
                  <strong>{selectedPlan.duration} days</strong>
                </div>
                <div className='summary-item'>
                  <span>Expected Profit</span>
                  <strong>{selectedPlan.expectedProfit}%</strong>
                </div>
                <div className='summary-item'>
                  <span>Investment Range</span>
                  <strong>{formatAmount(selectedPlan)}</strong>
                </div>
                <div className='summary-item'>
                  <span>Your Balance</span>
                  <strong>${user?.deposits?.toLocaleString() || 0}</strong>
                </div>
              </div>

              <form onSubmit={handleInvest}>
                <div className='form-group'>
                  <label>Investment Amount ($)</label>
                  <input
                    type='number'
                    value={investAmount}
                    onChange={(e) => setInvestAmount(e.target.value)}
                    min={selectedPlan.minAmount}
                    max={selectedPlan.maxAmount || undefined}
                    placeholder={`Min: $${selectedPlan.minAmount}`}
                    required
                  />
                </div>

                {investAmount && (
                  <div className='profit-preview'>
                    <p>
                      Expected Return:{' '}
                      <strong>
                        ${((Number(investAmount) * selectedPlan.expectedProfit) / 100).toLocaleString()}
                      </strong>
                    </p>
                    <p>
                      Total After {selectedPlan.duration} days:{' '}
                      <strong>
                        $
                        {(
                          Number(investAmount) +
                          (Number(investAmount) * selectedPlan.expectedProfit) / 100
                        ).toLocaleString()}
                      </strong>
                    </p>
                  </div>
                )}

                <div className='modal-actions'>
                  <button type='button' className='btn-cancel' onClick={closeModal}>
                    Cancel
                  </button>
                  <button type='submit' className='btn-confirm' disabled={submitting}>
                    {submitting ? 'Processing...' : 'Confirm Investment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}
    </main>
  );
};

export default TradingPlans;
